import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Package, Plus, MapPin, Lock, Sparkles, ShieldCheck, CreditCard, FileText, Pencil } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { money, STATUS_LABELS, STATUS_COLORS } from "../lib/helpers";

const STAGES = [
  ["all", "All"], ["pending_payment", "Awaiting Payment"], ["paid", "Ship to Us"],
  ["received", "Received"], ["grading", "In Grading"], ["graded", "Graded"],
  ["shipped", "Shipped Back"], ["cancelled", "Cancelled"],
];

const fireStandard = () => {
  confetti({ particleCount: 130, spread: 75, origin: { y: 0.6 } });
  setTimeout(() => confetti({ particleCount: 70, angle: 60, spread: 55, origin: { x: 0 } }), 150);
  setTimeout(() => confetti({ particleCount: 70, angle: 120, spread: 55, origin: { x: 1 } }), 150);
};
const fireGem = () => {
  const colors = ["#FFD700", "#FDB931", "#FFFFFF", "#4DB8E8"];
  const end = Date.now() + 1600;
  (function frame() {
    confetti({ particleCount: 7, angle: 60, spread: 65, origin: { x: 0 }, colors });
    confetti({ particleCount: 7, angle: 120, spread: 65, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  confetti({ particleCount: 180, spread: 110, origin: { y: 0.5 }, colors, scalar: 1.25, startVelocity: 45 });
};

export default function Portal() {
  const { user } = useAuth();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealing, setRevealing] = useState("");
  const [paying, setPaying] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/submissions/mine").then((r) => setSubs(r.data)).finally(() => setLoading(false));
  }, []);

  const reveal = async (id) => {
    setRevealing(id);
    try {
      await api.post(`/submissions/${id}/reveal`);
      const s = subs.find((x) => x.id === id);
      const gem = s?.cards?.some((c) => String(c.grade) === "10");
      setSubs((prev) => prev.map((x) => (x.id === id ? { ...x, grades_revealed: true } : x)));
      toast.success(gem ? "GEM MINT 10! 🏆" : "Grades revealed! 🎉");
      gem ? fireGem() : fireStandard();
    } catch { toast.error("Could not reveal grades. Please try again."); }
    finally { setRevealing(""); }
  };

  const payNow = async (id) => {
    setPaying(id);
    try {
      const { data } = await api.post("/payments/checkout", { submission_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Could not start checkout. Please try again."); setPaying(""); }
  };

  const cardLabel = (c) => [c.year, c.brand, c.subject].filter(Boolean).join(" ") || "Card";

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 md:py-20">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]">My Account</span>
          <h1 className="font-display font-extrabold text-5xl md:text-6xl tracking-tighter mt-4 text-[#0A0A0A]">Portal</h1>
          <p className="text-slate-600 mt-3 text-lg">Welcome back, {user?.name}. Track your grading submissions below.</p>
        </div>
        <Link to="/submit" data-testid="portal-new-submission" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-[#171717] transition-colors duration-300">
          <Plus size={16} /> New Submission
        </Link>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 no-print">
        {STAGES.map(([val, label]) => {
          const count = val === "all" ? subs.length : subs.filter((s) => s.status === val).length;
          if (val !== "all" && count === 0) return null;
          return (
            <button key={val} data-testid={`portal-filter-${val}`} onClick={() => setFilter(val)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors duration-200 ${filter === val ? "bg-[#0A0A0A] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#0A0A0A] hover:text-[#0A0A0A]"}`}>
              {label} <span className={filter === val ? "text-white/60" : "text-slate-400"}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-8 space-y-5">
        {loading ? (
          <p className="text-slate-400">Loading…</p>
        ) : subs.length === 0 ? (
          <div className="rounded-3xl border border-slate-200 bg-[#FAFAFA] p-16 text-center">
            <Package className="mx-auto text-slate-300 mb-4" size={44} />
            <p className="text-slate-600 text-lg">You have no submissions yet.</p>
            <Link to="/submit" className="inline-block mt-5 font-bold text-[#0A0A0A] hover:underline">Submit your first cards →</Link>
          </div>
        ) : (
          (filter === "all" ? subs : subs.filter((s) => s.status === filter)).map((s) => {
            const graded = s.status === "graded" || s.status === "shipped";
            const revealed = !!s.grades_revealed;
            return (
              <div key={s.id} data-testid={`submission-${s.id}`} className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-display font-bold text-xl text-[#0A0A0A]">{s.tier_name}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border ${STATUS_COLORS[s.refunded ? "cancelled" : s.status] || ""}`}>{s.refunded ? "Cancelled & Refunded" : (STATUS_LABELS[s.status] || s.status)}</span>
                    </div>
                    <p className="text-slate-400 text-xs mt-1.5">Order #{s.id.slice(-8)} · {new Date(s.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-extrabold text-3xl text-[#0A0A0A]">{money(s.total_amount)}</div>
                    <div className="text-xs text-slate-400">{s.card_count} card(s)</div>
                  </div>
                </div>

                {/* Grade reveal experience */}
                {graded && !revealed ? (
                  <div className="mt-5 rounded-2xl bg-[#0A0A0A] text-white p-6 text-center" data-testid={`reveal-block-${s.id}`}>
                    <Sparkles className="mx-auto text-[#4DB8E8] mb-3" size={26} />
                    <h3 className="font-display font-extrabold text-2xl tracking-tight">Your grades are ready</h3>
                    <p className="text-white/55 text-sm mt-2 max-w-md mx-auto">We've kept them hidden to preserve the surprise. Reveal now, or wait to crack open your physical slabs.</p>
                    <button data-testid={`reveal-btn-${s.id}`} onClick={() => reveal(s.id)} disabled={revealing === s.id}
                      className="mt-5 inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#0A0A0A] font-bold rounded-full hover:bg-neutral-200 transition-colors duration-300 disabled:opacity-50">
                      <Lock size={15} /> {revealing === s.id ? "Revealing…" : "Reveal my grades"}
                    </button>
                  </div>
                ) : graded && revealed ? (
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`grades-${s.id}`}>
                    {s.cards.map((c, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 bg-[#FAFAFA] rounded-xl px-4 py-3 border border-slate-100">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-[#0A0A0A] truncate">{cardLabel(c)}</div>
                          {c.certnum && <div className="text-[10px] text-slate-400 font-bold">CERT {c.certnum}</div>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="text-right">
                            <div className="font-display font-900 text-2xl text-[#0A0A0A] leading-none" style={{ fontWeight: 900 }}>{c.grade || "—"}</div>
                            {c.label && <div className="text-[9px] font-bold uppercase tracking-widest text-[#4DB8E8]">{c.label}</div>}
                          </div>
                          {c.certnum && (
                            <Link to={`/lookup/${c.certnum}`} data-testid={`verify-${c.certnum}`} className="p-2 rounded-lg border border-slate-200 hover:border-[#0A0A0A] transition-colors" title="Verify cert">
                              <ShieldCheck size={16} className="text-[#0A0A0A]" />
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {s.cards.map((c, i) => (
                      <div key={i} className="text-sm text-slate-600 bg-[#FAFAFA] rounded-xl px-4 py-2.5 border border-slate-100">
                        {cardLabel(c)} {c.cardnum && <span className="text-slate-400">#{c.cardnum}</span>}
                      </div>
                    ))}
                  </div>
                )}

                {s.tracking && <p className="text-xs text-[#0A0A0A] mt-4 flex items-center gap-1.5"><MapPin size={13} /> Tracking: <strong>{s.tracking}</strong></p>}
                {s.payment_status === "paid" && (
                  <Link to={`/packing-slip/${s.id}`} data-testid={`slip-${s.id}`} className="text-xs font-bold text-[#0A0A0A] mt-4 inline-flex items-center gap-1.5 hover:underline"><FileText size={13} /> Print packing slip</Link>
                )}
                {s.status === "pending_payment" && (
                  <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-amber-50 border border-amber-200 p-4">
                    <p className="text-sm text-amber-800 font-semibold">Payment not completed for this order.</p>
                    <div className="flex items-center gap-2 shrink-0">
                      <Link to={`/submit?edit=${s.id}`} data-testid={`edit-order-${s.id}`}
                        className="inline-flex items-center gap-2 px-5 py-3 border-2 border-[#0A0A0A] text-[#0A0A0A] text-sm font-bold rounded-full hover:bg-[#0A0A0A] hover:text-white transition-colors duration-300">
                        <Pencil size={15} /> Edit &amp; Review
                      </Link>
                      <button data-testid={`pay-now-${s.id}`} onClick={() => payNow(s.id)} disabled={paying === s.id}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A0A0A] text-white text-sm font-bold rounded-full hover:bg-[#171717] transition-colors duration-300 disabled:opacity-50">
                        <CreditCard size={15} /> {paying === s.id ? "Redirecting…" : `Pay ${money(s.total_amount)}`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
